import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import { roleClassificationKeys } from "@/lib/query";
import type {
  RoleClassificationCreateInput,
  RoleClassificationUpdateInput,
} from "@/schemas/role-classifications";

export interface RoleClassification {
  id: string;
  /** Code-stable token stored on `person.role_classification`. Never editable. */
  key: string;
  /** Admin-editable display label. */
  name: string;
  /** NOS's own name for this classification. NULL = can't be written to NOS. */
  nosName: string | null;
  sortOrder: number;
  isActive: boolean;
  /** How many people hold this classification — one in use can't be deleted. */
  personCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Every classification, active and inactive, in admin order. Inactive ones are
 * included so a person who still holds a retired classification renders with
 * its real label rather than a raw token — pickers filter with
 * `useRoleClassificationOptions`.
 */
export function useRoleClassifications(enabled = true) {
  return useQuery({
    queryKey: roleClassificationKeys.all,
    queryFn: async (): Promise<RoleClassification[]> => {
      const res = await fetch("/api/role-classifications");
      if (!res.ok) throw new Error("Failed to fetch role classifications");
      const json = await res.json();
      return json.data;
    },
    enabled,
  });
}

/**
 * Picker options (active only) plus a token → label map that covers retired
 * ones too. Every role-classification `<Select>`, filter and badge reads from
 * here, so there is no second copy of the list anywhere in the client.
 */
export function useRoleClassificationOptions() {
  const query = useRoleClassifications();
  const rows = useMemo(() => query.data ?? [], [query.data]);
  return useMemo(
    () => ({
      isLoading: query.isLoading,
      options: rows.filter((r) => r.isActive).map((r) => ({ value: r.key, label: r.name })),
      labelFor: (key: string) => rows.find((r) => r.key === key)?.name ?? key,
    }),
    [rows, query.isLoading],
  );
}

async function mutate(url: string, method: string, body?: unknown) {
  const res = await fetch(url, {
    method,
    ...(body !== undefined && {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  });
  if (!res.ok) {
    // The delete and create refusals carry a specific message (one in use, a
    // built-in, a duplicate key); surfacing it is the whole point.
    const json = await res.json().catch(() => null);
    throw new Error(json?.error ?? "Request failed");
  }
  return res.status === 204 ? null : res.json().catch(() => null);
}

export function useCreateRoleClassification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RoleClassificationCreateInput) =>
      mutate("/api/role-classifications", "POST", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleClassificationKeys.all });
      toast.success("Role classification created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateRoleClassification(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RoleClassificationUpdateInput) =>
      mutate(`/api/role-classifications/${id}`, "PUT", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleClassificationKeys.all });
      toast.success("Role classification updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useReorderRoleClassification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: "up" | "down" }) =>
      mutate(`/api/role-classifications/${id}/reorder`, "PATCH", { direction }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleClassificationKeys.all });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeleteRoleClassification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mutate(`/api/role-classifications/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleClassificationKeys.all });
      toast.success("Role classification deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
